import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { PORTAL_STORAGE_KEYS, SUPPORTED_PORTAL_LOCALES } from '@heglo/ui/tokens';
import i18n from '@/i18n';

type PortalLanguage = 'ar' | 'en';

function applyLanguage(language: PortalLanguage) {
  document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.lang = language;
  void i18n.changeLanguage(language);
}

function hasStoredLanguagePreference(): boolean {
  return Boolean(
    localStorage.getItem(PORTAL_STORAGE_KEYS.uiSettings)
    || localStorage.getItem(PORTAL_STORAGE_KEYS.legacyUiSettings),
  );
}

interface UIState {
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark';
  language: PortalLanguage;
  enabledLanguages: PortalLanguage[];
  defaultLanguage: PortalLanguage;
  toggleSidebar: () => void;
  toggleSidebarCollapse: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  setLanguage: (language: PortalLanguage) => void;
  setTenantLanguages: (enabled: PortalLanguage[], defaultLanguage: PortalLanguage) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      sidebarOpen: true,
      sidebarCollapsed: false,
      theme: 'light',
      language: 'ar',
      enabledLanguages: [...SUPPORTED_PORTAL_LOCALES],
      defaultLanguage: 'ar',

      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      toggleSidebarCollapse: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setTheme: (theme) => set({ theme }),
      setLanguage: (language) => {
        if (!get().enabledLanguages.includes(language)) return;
        applyLanguage(language);
        set({ language });
      },
      setTenantLanguages: (enabled, defaultLanguage) => {
        const enabledLanguages = enabled.length > 0 ? enabled : [...SUPPORTED_PORTAL_LOCALES];
        const preferred = hasStoredLanguagePreference() && enabledLanguages.includes(get().language)
          ? get().language
          : enabledLanguages.includes(defaultLanguage)
            ? defaultLanguage
            : enabledLanguages[0];
        applyLanguage(preferred);
        set({
          enabledLanguages,
          defaultLanguage: enabledLanguages.includes(defaultLanguage) ? defaultLanguage : enabledLanguages[0],
          language: preferred,
        });
      },
    }),
    {
      name: PORTAL_STORAGE_KEYS.uiSettings,
      // Rehydrate the legacy preference as well as reading it in i18n. Without
      // this, Zustand immediately overwrites a returning user's language with
      // the default Arabic state after a branding/storage-key migration.
      storage: createJSONStorage(() => ({
        getItem: (name) => localStorage.getItem(name)
          ?? localStorage.getItem(PORTAL_STORAGE_KEYS.legacyUiSettings),
        setItem: (name, value) => localStorage.setItem(name, value),
        removeItem: (name) => {
          localStorage.removeItem(name);
          localStorage.removeItem(PORTAL_STORAGE_KEYS.legacyUiSettings);
        },
      })),
      onRehydrateStorage: () => (state) => {
        if (state) applyLanguage(state.language);
      },
    },
  ),
);
